import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { FileText, MessageSquareQuote, Plus } from "lucide-react";

export default function InsertPanel({ ticketId, onInsert }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [res, setRes] = useState({ articles: [], snippets: [] });

  const load = useCallback(async () => {
    if (!open) return;
    try {
      const r = await api.get("/compose/search", { params: { q, ticket_id: ticketId } });
      setRes(r.data);
    } catch { setRes({ articles: [], snippets: [] }); }
  }, [open, q, ticketId]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  function take(text) { onInsert(text); setOpen(false); setQ(""); }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid="insert-panel-trigger"
                className="inline-flex items-center gap-1.5 text-sm px-2.5 py-1.5 rounded
                           border border-zinc-800 text-zinc-300 hover:bg-zinc-800">
          <Plus className="h-3.5 w-3.5" /> Insert
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[26rem] p-0 bg-zinc-950 border-zinc-800" align="start">
        <input
          autoFocus value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search snippets and articles…"
          data-testid="insert-panel-search"
          className="w-full bg-transparent border-b border-zinc-800 px-3 py-2 text-sm outline-none"
        />
        <div className="max-h-80 overflow-auto">
          {res.snippets.length > 0 && (
            <div className="px-3 py-2 text-[11px] uppercase tracking-wide text-zinc-500">Snippets</div>
          )}
          {res.snippets.map((s) => (
            <button key={s.id} type="button" onClick={() => take(s.preview)}
                    data-testid={`insert-snippet-${s.id}`}
                    className="w-full text-left px-3 py-2 hover:bg-zinc-900 block">
              <div className="flex items-center gap-2 text-sm text-zinc-200">
                <MessageSquareQuote className="h-3.5 w-3.5 text-zinc-500" />
                {s.title}
                {s.owner_id === null && (
                  <span className="text-[10px] text-zinc-500 border border-zinc-800 rounded px-1">shared</span>
                )}
              </div>
              <div className="text-xs text-zinc-500 truncate">{(s.preview || "").split("\n")[0]}</div>
              {s.unresolved?.length > 0 && (
                // Named, not silently blanked. "Hi , as discussed" is worse than
                // a visible placeholder, because only one of them looks broken.
                <div className="text-[11px] text-amber-500/80 mt-0.5">
                  {s.unresolved.length} placeholder{s.unresolved.length > 1 ? "s" : ""} could not be
                  filled: {s.unresolved.join(", ")}
                </div>
              )}
            </button>
          ))}

          {res.articles.length > 0 && (
            <div className="px-3 py-2 text-[11px] uppercase tracking-wide text-zinc-500">Articles</div>
          )}
          {res.articles.map((a) => (
            <button key={a.id} type="button"
                    onClick={() => take(`[${a.title}](/knowledge/${a.id})\n\n${a.summary}`)}
                    data-testid={`insert-article-${a.id}`}
                    className="w-full text-left px-3 py-2 hover:bg-zinc-900 block">
              <div className="flex items-center gap-2 text-sm text-zinc-200">
                <FileText className="h-3.5 w-3.5 text-zinc-500" />{a.title}
              </div>
              <div className="text-xs text-zinc-500">{a.category}</div>
            </button>
          ))}

          {res.snippets.length === 0 && res.articles.length === 0 && (
            <div className="px-3 py-6 text-center text-sm text-zinc-500">Nothing matches.</div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
